import type { Role } from '../../queries/teamQuery';
import type { ZustandStore } from '../store.type';
import type { NamedSet } from 'zustand/middleware';

export type PhoneDetails = {
	phoneCountryCode: string;
	phoneNumber: string;
	fullPhoneNumber: string;
};

export type User = {
	userId: string;
	email: string;
	firstName: string;
	lastName: string;
	fullName: string;
	companyAccountId: string;
	companyName: string;
	initials: string;
	language: string;
	roleId: string;
	roles: Role[];
	state: string;
	isSupportAdmin: boolean;
	properties: Record<string, unknown>;
	userSelectedTeam: string;
	avatarUrl: string;
	invitedUser: User;
	canForwardApprovals: boolean;

	userAvatarToRenderInUi?: string;

	phoneDetails: PhoneDetails | null;
};

export type UserState = {
	user: User;
};

type UserActions = {
	setUser: (user: UserState['user']) => void;
	setUserSelectedTeam: (selectedTeam: UserState['user']['userSelectedTeam']) => void;
	setInvitedUser: (invitedUser: UserState['user']['invitedUser']) => void;
	setCanForwardApprovals: (canForwardApprovals: UserState['user']['canForwardApprovals']) => void;
};

export type UserSlice = UserState & UserActions;

const userInitialState: UserState = {
	user: {
		userId: '',
		email: '',
		firstName: '',
		lastName: '',
		fullName: '',
		companyAccountId: '',
		companyName: '',
		initials: '',
		language: '',
		roleId: '',
		roles: [],
		state: '',
		isSupportAdmin: false,
		properties: {},
		userSelectedTeam: '',
		avatarUrl: '',
		invitedUser: {} as User,
		canForwardApprovals: false,
		phoneDetails: null,
	},
};

export function userSlice(set: NamedSet<ZustandStore>): UserSlice {
	return {
		...userInitialState,
		setUser(user: UserState['user']): void {
			set(
				(state: UserState) => {
					state.user = { ...state.user, ...user };
					return state;
				},
				false,
				'[setUser]',
			);
		},
		setUserSelectedTeam(selectedTeam: UserState['user']['userSelectedTeam']): void {
			set(
				(state: UserState) => {
					state.user.userSelectedTeam = selectedTeam;
					return state;
				},
				false,
				'[setUserSelectedTeam]',
			);
		},
		setInvitedUser(invitedUser: UserState['user']['invitedUser']): void {
			set(
				(state: UserState) => {
					state.user.invitedUser = invitedUser;
					return state;
				},
				false,
				'[setInvitedUser]',
			);
		},
		setCanForwardApprovals(canForwardApprovals: UserState['user']['canForwardApprovals']): void {
			set(
				(state: UserState) => {
					state.user.canForwardApprovals = canForwardApprovals;
					return state;
				},
				false,
				'[setCanForwardApprovals]',
			);
		},
	};
}
